/**
 * Generic stats filter presets, persisted on Kore user↔instance `config`:
 *   config.stats.filterPresetsByScope.<scope>[]
 *
 * List arrays are replaced wholesale on merge — always PUT the full list
 * for the scope being updated.
 */

import UsersApi from './rest/Users';
import {
    STATS_FILTER_PRESET_SCOPES,
    buildFilterPreset,
    buildScopePresetsPatch,
    captureAnalysisFilterPresetSnapshot,
    captureAttendanceAnalysisFilters,
    presetsFromConfig,
    presetDatePresetKey,
    removePresetFromList,
    renamePresetInList,
    upsertPresetInList,
} from './statsFilterPresetsCore';

export {
    STATS_FILTER_PRESET_SCOPES,
    buildFilterPreset,
    buildScopePresetsPatch,
    captureAnalysisFilterPresetSnapshot,
    captureAttendanceAnalysisFilters,
    presetsFromConfig,
    presetDatePresetKey,
    removePresetFromList,
    renamePresetInList,
    upsertPresetInList,
};

/**
 * @param {string} tktInstanceId
 * @param {string} scope
 * @returns {Promise<Array<object>>}
 */
export async function listFilterPresets(tktInstanceId, scope) {
    if (!tktInstanceId || !scope) {
        return [];
    }
    const config = await UsersApi.getInstanceConfig(tktInstanceId);
    return presetsFromConfig(config, scope);
}

/**
 * Replace the full preset list for one scope (server replaces list arrays).
 *
 * @param {string} tktInstanceId
 * @param {string} scope
 * @param {Array<object>} presets
 * @returns {Promise<Array<object>>}
 */
export async function replaceFilterPresets(tktInstanceId, scope, presets) {
    if (!tktInstanceId || !scope) {
        throw new Error('Missing instance or scope');
    }
    const config = await UsersApi.mergeInstanceConfig(
        tktInstanceId,
        buildScopePresetsPatch(scope, presets),
    );
    return presetsFromConfig(config, scope);
}

/**
 * @param {string} tktInstanceId
 * @param {string} scope
 * @param {{ id?: string, name: string, filters: object }} input
 * @returns {Promise<{ presets: Array<object>, preset: object }>}
 */
export async function saveFilterPreset(tktInstanceId, scope, input) {
    const current = await listFilterPresets(tktInstanceId, scope);
    const preset = buildFilterPreset({ ...input, scope });
    const next = upsertPresetInList(current, preset);
    const presets = await replaceFilterPresets(tktInstanceId, scope, next);
    const saved = presets.find((p) => p.id === preset.id) || preset;
    return { presets, preset: saved };
}

/**
 * @param {string} tktInstanceId
 * @param {string} scope
 * @param {string} id
 * @param {string} name
 * @returns {Promise<Array<object>>}
 */
export async function renameFilterPreset(tktInstanceId, scope, id, name) {
    const current = await listFilterPresets(tktInstanceId, scope);
    const next = renamePresetInList(current, id, name);
    return replaceFilterPresets(tktInstanceId, scope, next);
}

/**
 * @param {string} tktInstanceId
 * @param {string} scope
 * @param {string} id
 * @returns {Promise<Array<object>>}
 */
export async function deleteFilterPreset(tktInstanceId, scope, id) {
    const current = await listFilterPresets(tktInstanceId, scope);
    const next = removePresetFromList(current, id);
    return replaceFilterPresets(tktInstanceId, scope, next);
}
